import { cx } from '@linaria/core';
import { useState, useRef, useEffect } from 'react';
import { Button } from '@ui/kit';
import { useUpdateDialogueMutation } from './api.update-dialogue.mutation.js';
import { inputWrapper, input, inputPlaceholder, sendButton } from './atom.input.style.js';
import { sizes } from './overlay.dialog.style.js';
import { jsxs, jsx } from 'react/jsx-runtime';

function DialogueInput(props) {
  const mutate = useUpdateDialogueMutation();
  const [empty, setEmpty] = useState(true);
  const inputRef = useRef(null);
  const onSubmit = () => {
    const input = inputRef.current;
    if (!input || input.innerText.trim() === "") {
      return;
    }
    mutate.mutate({
      dialogueId: props.dialogueId,
      message: input.innerText
    });
    input.innerHTML = "";
  };
  const prevHeightRef = useRef(inputRef.current?.clientHeight ?? sizes.footerMinHeight);
  const onKeyDown = evt => {
    switch (evt.key) {
      case "Escape":
        if (inputRef.current && inputRef.current.innerText !== "") {
          evt.stopPropagation();
          inputRef.current.innerHTML = "";
        }
        break;
      case "Enter":
        if (!evt.shiftKey) {
          evt.preventDefault();
          onSubmit();
        }
        break;
    }
  };

  // ensure that pasted content is inserted unformatted with correctly placed
  // caret afterwards.
  const onPaste = evt => {
    evt.stopPropagation();
    evt.preventDefault();
    const input = inputRef.current;
    if (!input) {
      return;
    }
    const selection = window.getSelection();
    const text = evt.clipboardData.getData("Text");
    if (selection && selection.rangeCount > 0) {
      const range = selection?.getRangeAt(0);
      if (range.startContainer === input || input.contains(range.commonAncestorContainer)) {
        range.deleteContents();
        range.insertNode(document.createTextNode(text));
        range.collapse();
      }
    } else {
      input.innerText += text;
    }
  };

  // ensure that the once the message nearest to the input always remains
  // visible, even when the height of the input changes by manipulating the
  // message container scrollTop.
  useEffect(() => {
    const input = inputRef.current;
    const messagesContainer = props.messagesContainerRef.current;
    if (!input) {
      return;
    }
    const onHeightChange = () => {
      if (!input || !messagesContainer) {
        return;
      }
      const diff = input.clientHeight - prevHeightRef.current;
      const minimum = messagesContainer.scrollTop - (messagesContainer.scrollHeight - messagesContainer.clientHeight);
      messagesContainer.scrollTop += Math.max(diff, minimum);
      prevHeightRef.current = input.clientHeight;
    };
    const observer = new ResizeObserver(onHeightChange);
    observer.observe(input);
    return () => {
      observer.disconnect();
    };
  }, [props.messagesContainerRef]);

  // Annoyingly Safari does not implement the :empty selector properly, and
  // as such we must perform empty detection ourselves.
  useEffect(() => {
    const input = inputRef.current;
    if (!input) {
      return;
    }
    const onChange = () => {
      const text = input.innerText;
      const newLines = text.split("\n").length;
      setEmpty(!text.trim() && newLines <= 2 && text.length < newLines);
    };
    const observer = new MutationObserver(onChange);
    observer.observe(input, {
      subtree: true,
      childList: true
    });
    return () => {
      observer.disconnect();
    };
  }, []);
  return /*#__PURE__*/jsxs("div", {
    className: inputWrapper,
    children: [/*#__PURE__*/jsx("div", {
      ref: inputRef,
      className: input,
      contentEditable: true,
      role: "textbox",
      "aria-label": "Skriv ny besked",
      spellCheck: true,
      tabIndex: 0,
      onPaste: onPaste,
      onKeyDownCapture: onKeyDown
    }), /*#__PURE__*/jsx("span", {
      "aria-hidden": true,
      className: cx(inputPlaceholder, empty && "visible"),
      children: "Skriv besked"
    }), /*#__PURE__*/jsx(Button, {
      onClick: onSubmit,
      className: sendButton,
      theme: "primary",
      layout: "small",
      children: "Send"
    })]
  });
}

export { DialogueInput };
//# sourceMappingURL=atom.input.js.map
